import { SeverityLevel } from '@microsoft/applicationinsights-web';
import * as signalR from '@microsoft/signalr';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../../../api/generated';
import { KendoLoading } from '../../../components';
import * as enums from '../../../enums';
import { deviceType, ScreenSoundType } from '../../../enums';
import '../../../styles/evacuation.scss';
import '../../../styles/workSchedule.scss';
import { ai } from '../../../utils';
import { IInfoScreenColorsForm } from '../../administration/organization/generalSettings/design/GeneralSettingsDesignInfoScreen';
import { ShowErrorModal } from '../../queueManagement/components/ShowErrorModal';
import InfoScreenDisplayLocations, { IVisit } from './InfoScreenDisplayLocations';
import InfoScreenDisplayWorkSchedules from './InfoScreenDisplayWorkSchedules';

const InfoScreenDisplayPage: FC = () => {
    const [infoScreenDisplayType, setInfoScreenDisplayType] = useState<enums.InfoScreenDisplayType | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { infoScreenId } = useParams<'infoScreenId'>();
    const [evacuate, setEvacuate] = useState<boolean>(false);
    const [design, setDesign] = useState<IInfoScreenColorsForm | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [newVisit, setNewVisit] = useState<IVisit>();
    const [soundType, setSoundType] = useState<ScreenSoundType>();

    var connection: signalR.HubConnection;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await api.display.getInfoScreenInitialSettings(parseInt(infoScreenId.toString()));
                if (response) {
                    setSoundType(response.soundType);
                    const button = document.getElementById('playButton');
                    if (button) button!.innerText = response.soundType.toString();
                    setInfoScreenDisplayType(response.displayType);
                    setEvacuate(response.evacuation);
                    setDesign(response.design);
                }
            } catch (ex) {
                setError(ex.error);
                ai.appInsights.trackException({
                    exception: new Error('InfoScreenDisplayPage Error in useEffect fetchData' + ex + ', ' + ex?.error),
                    severityLevel: SeverityLevel.Error
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const initializeSignalR = () => {
            const connection = new signalR.HubConnectionBuilder()
                .withUrl(`/workplaces/?infoScreenId=${infoScreenId.toString()}`)
                .withAutomaticReconnect([2000, 3000, 5000, 10000, 20000, 30000, 60000, 60000])
                .build();

            connection.onreconnecting(() => {
                if (connection.state === signalR.HubConnectionState.Reconnecting) {
                    setError('Nėra interneto ryšio. Bandoma prisijungti...');
                }
            });
            connection.onreconnected(() => {
                if (connection.state === signalR.HubConnectionState.Connected) {
                    setError('Prisijungta. Įkeliamas vaizdas...');
                }

                window.location.reload();
            });
            connection.onclose(async () => {
                if (connection.state === signalR.HubConnectionState.Disconnected) {
                    setError('Nėra interneto ryšio.');
                }
            });
            connection.on('onRefreshInfoScreen', () => {
                window.location.reload();
            });

            connection.on('onChangeEvacuationState', (isEvacuationEnabled: boolean) => {
                try {
                    setEvacuate(isEvacuationEnabled);
                } catch (ex) {
                    ai.appInsights.trackException({
                        exception: new Error('InfoScreenDisplayPage Error in hub connection: onChangeEvacuationState' + ex + ', ' + ex?.error),
                        severityLevel: SeverityLevel.Error
                    });
                }
            });
            connection.on('onChangeInfoScreensDesign', (design: IInfoScreenColorsForm) => {
                try {
                    setDesign(design);
                } catch (ex) {
                    ai.appInsights.trackException({
                        exception: new Error('InfoScreenDisplayPage Error in hub connection: onChangeInfoScreensDesign' + ex + ', ' + ex?.error),
                        severityLevel: SeverityLevel.Error
                    });
                }
            });

            //InfoScreenDisplayLocations
            connection.on('onNextTicket', (visitToLoad: IVisit) => {
                try {
                    setNewVisit(visitToLoad);
                } catch (ex) {
                    ai.appInsights.trackException({
                        exception: new Error('InfoScreenDisplayPage Error in hub connection: onNextTicket' + ex + ', ' + ex?.error),
                        severityLevel: SeverityLevel.Error
                    });
                }
            });

            //InfoScreenDisplayLocations
            connection.on('onChangeSoundType', (type: ScreenSoundType) => {
                try {
                    if (type) {
                        setSoundType(type);
                        const button = document.getElementById('playButton');
                        if (button) button!.innerText = type.toString();
                    } else {
                        ai.appInsights.trackException({
                            exception: new Error(
                                'InfoScreenLocations no data onChangeSoundType ' + document.getElementById('playButton')?.innerText.toString()
                            ),
                            severityLevel: SeverityLevel.Error
                        });
                    }
                } catch (ex) {
                    ai.appInsights.trackException({
                        exception: new Error('InfoScreenLocations Error in hub connection: onChangeSoundType' + ex + ', ' + ex?.error),
                        severityLevel: SeverityLevel.Error
                    });
                }
            });

            connection.start().catch((ex) => {
                ai.appInsights.trackException({
                    exception: new Error('InfoScreenDisplayPage Error in hub connection: first start' + ex + ', ' + ex?.error),
                    severityLevel: SeverityLevel.Error
                });
            });
            return connection;
        };

        connection = initializeSignalR();

        return () => {
            connection.stop();
        };
    }, []);

    useEffect(() => {
        const updateLastConnectionInterval = setInterval(async () => {
            if (!error && connection) {
                try {
                    await connection.invoke('updateDeviceLastConnection', parseInt(infoScreenId.toString(), 10), deviceType.InfoScreen);
                } catch (ex) {
                    ai.appInsights.trackException({
                        exception: new Error('InfoScreenDisplayPage Error in updateLastConnectionInterval' + ex + ', ' + ex?.error),
                        severityLevel: SeverityLevel.Error
                    });
                }
            }
        }, 60000);

        return () => {
            clearInterval(updateLastConnectionInterval);
        };
    }, [error]);

    const getDisplayComponent = () => {
        if (error) {
            return <ShowErrorModal error={error} logout={false}></ShowErrorModal>;
        }

        if (evacuate) {
            return (
                <div className="evacuation-infoScreen">
                    <div className="split left danger">
                        <div className="danger-text centered">Pavojus!</div>
                        <img className="danger-image" src={process.env.PUBLIC_URL + '/img/bell.png'} />
                    </div>
                    <img className="divider" src={process.env.PUBLIC_URL + '/img/divider.png'} />
                    <div className="split right noElevator">
                        <div className="text">
                            Nesinaudokite <br /> liftu!
                        </div>

                        <img className="image" src={process.env.PUBLIC_URL + '/img/noElevator.png'} />
                    </div>
                    <div className="evacuate">
                        <div className="text">
                            Naudokitės evakuaciniais <br /> išėjimais
                        </div>
                        <img className="image" src={process.env.PUBLIC_URL + '/img/evacuate.png'} />
                    </div>
                </div>
            );
        }

        switch (infoScreenDisplayType) {
            case enums.infoScreenDisplayType.Locations:
                return (
                    <InfoScreenDisplayLocations
                        id={parseInt(infoScreenId.toString())}
                        setError={setError}
                        design={design}
                        newVisit={newVisit!}
                        soundType={soundType!}
                    />
                );
            case enums.infoScreenDisplayType.Schedule:
                return <InfoScreenDisplayWorkSchedules id={parseInt(infoScreenId.toString())} setError={setError} design={design} />;
            default:
                return <>Įrenginio tipas neturi atvaizdavimo komponento</>;
        }
    };

    return isLoading ? <KendoLoading /> : getDisplayComponent();
};

export default InfoScreenDisplayPage;
